import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { convertToVwWeb } from '../../Utils/responsive';
import kakaoModalImg from '../../assets/img/kakao_modal_img.png'; // Assuming this exists or I need to use a placeholder
import { authRequest } from '../../core/api/authApiClient';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { getUserDetails, updateUser } from '../../Module/User/Presentation/userActions';
import { selectUserProfile } from '../../Module/User/Presentation/userSelectors';
import { isIndividualUserType } from '../../Utils/userType';
import type { UpdateUserRequest } from '../../Module/User/Domain/entity/UserTypes';

// Declare Kakao on window
declare global {
  interface Window {
    Kakao: any;
  }
}

const KAKAO_KEY = 'd7fe97bd56b8e854e11feab6313e4b0a';
const REDIRECT_URI = 'https://on.missionfund.org/project';

interface KakaoChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const KakaoChannelModal: React.FC<KakaoChannelModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(selectUserProfile);
  const processedCodeRef = useRef<string>('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code')?.trim();
    if (!code || processedCodeRef.current === code) return;
    processedCodeRef.current = code;

    const exchangeKakaoCode = async () => {
      try {
        const response = await authRequest<{
          result_Code?: number;
          result_code?: number;
          result_message?: string;
          statusCode?: number;
          data?: {
            kakaoId?: string | number;
            phoneNumber?: string;
            profileImage?: string;
          };
        }>('/auth/kakao/token', {
          body: { code },
        });
        const resultCode = response?.result_Code ?? response?.result_code;
        if (
          (typeof response?.statusCode === 'number' && response.statusCode !== 200) ||
          (typeof resultCode === 'number' && resultCode !== 0)
        ) {
          throw new Error(response?.result_message || '카카오 인증 토큰 교환 실패');
        }

        const userId = window.localStorage.getItem('missionfund.userId');
        if (userId) {
          const kakaoData = response?.data;
          const kakaoIdText =
            kakaoData?.kakaoId === undefined || kakaoData?.kakaoId === null
              ? ''
              : String(kakaoData.kakaoId).trim();
          const sns_id = kakaoIdText
            ? kakaoIdText.startsWith('user')
              ? kakaoIdText
              : `user${kakaoIdText}`
            : undefined;
          const sns_phone =
            typeof kakaoData?.phoneNumber === 'string' && kakaoData.phoneNumber.trim()
              ? kakaoData.phoneNumber.trim()
              : undefined;
          const sns_thumbnail =
            typeof kakaoData?.profileImage === 'string' && kakaoData.profileImage.trim()
              ? kakaoData.profileImage.trim()
              : undefined;

          if (sns_id || sns_phone || sns_thumbnail) {
            const restrictedRegion =
              typeof userProfile?.restricted_region === 'boolean'
                ? userProfile.restricted_region
                  ? 'true'
                  : 'false'
                : userProfile?.restricted_region === undefined
                ? undefined
                : String(userProfile.restricted_region);

            const request: UpdateUserRequest = {
              user_id: userId,
              first_name: userProfile?.first_name,
              last_name: userProfile?.last_name,
              email: userProfile?.email,
              partner: userProfile?.partner,
              phone:
                isIndividualUserType(userProfile?.user_type) && sns_phone
                  ? sns_phone
                  : userProfile?.phone,
              user_type: userProfile?.user_type,
              country_code: userProfile?.country_code,
              organization: userProfile?.organization,
              mission_account_no: userProfile?.mission_account_no,
              language_type: userProfile?.language_type,
              birth_date: userProfile?.birth_date,
              bank_location_country_code: userProfile?.bank_location_country_code,
              bank_name: userProfile?.bank_name,
              bank_account: userProfile?.bank_account,
              bank_account_holder: userProfile?.bank_account_holder,
              address1: userProfile?.address1,
              address2: userProfile?.address2,
              city: userProfile?.city,
              state_province: userProfile?.state_province,
              zipcode: userProfile?.zipcode,
              paypal_email: userProfile?.paypal_email,
              authentication_by: userProfile?.authentication_by,
              access_at: userProfile?.access_at,
              routing_no: userProfile?.routing_no,
              swift_code: userProfile?.swift_code,
              bank_address: userProfile?.bank_address,
              mission_field: userProfile?.mission_field,
              sending_church: userProfile?.sending_church,
              restricted_region: restrictedRegion,
              business_registration: userProfile?.business_registration,
              user_desc: userProfile?.user_desc,
              image_url: userProfile?.image_url,
              authentication_image_url: userProfile?.authentication_image_url,
              registration_card_url: userProfile?.registration_card_url,
              bankbook_image_url: userProfile?.bankbook_image_url,
              sns_id,
              sns_phone,
              sns_thumbnail,
            };

            const filteredRequest = Object.fromEntries(
              Object.entries(request).filter(([, value]) => value !== undefined)
            ) as UpdateUserRequest;

            await dispatch(updateUser(filteredRequest)).unwrap();
          }

          dispatch(getUserDetails(userId));
        }

        params.delete('code');
        const next = params.toString();
        const nextUrl = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, '', nextUrl);
      } catch (error) {
        console.error('Failed to exchange Kakao auth code:', error);
      }
    };

    exchangeKakaoCode();
  }, [dispatch, userProfile]);

  useEffect(() => {
    if (!isOpen) return;

    const initializeKakao = () => {
      try {
        if (!window.Kakao) {
          console.error('Kakao SDK not loaded');
          return;
        }
        if (!window.Kakao.isInitialized()) {
          window.Kakao.init(KAKAO_KEY);
        }
      } catch (e) {
        console.error('Failed to initialize Kakao SDK:', e);
      }
    };

    if (window.Kakao) {
      initializeKakao();
    } else {
      const script = document.createElement('script');
      script.src = 'https://t1.kakaocdn.net/kakao_js_sdk/2.6.0/kakao.min.js';
      // Integrity check failed, so we remove it for now to allow loading
      // script.integrity = 'sha384-6MFdIr0zOira1CHQkedUqJVqe0ryQtcLs7ufRWw+T8rCXNKryCOgM/c7x5+z2JK7';
      script.crossOrigin = 'anonymous';
      script.onload = initializeKakao;
      script.onerror = (e) => console.error('Failed to load Kakao SDK script:', e);
      document.head.appendChild(script);
    }
  }, [isOpen]);

  const handleKakaoAuth = () => {
    if (!window.Kakao) {
      console.error('Kakao SDK not loaded');
      return;
    }

    if (!window.Kakao.isInitialized()) {
      window.Kakao.init(KAKAO_KEY);
    }

    if (window.Kakao.Auth) {
      // For demo/development purpose only:
      // Since we can't handle the actual callback on localhost without proper backend setup,
      // we'll simulate a successful authentication flow here.
      // alert('카카오 인증 페이지로 이동합니다.');
      
      window.Kakao.Auth.authorize({
        redirectUri: REDIRECT_URI,
      });
    } else {
      console.error('Kakao.Auth is not available');
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <ImageWrapper>
          <img src={kakaoModalImg} alt="Kakao Channel" onError={(e) => e.currentTarget.style.display = 'none'} />
        </ImageWrapper>
        <TextWrapper>
          <MainText>본인확인을 위해 카카오 간편가입</MainText>
          <MainText>및 인증을 마무리해주세요.</MainText>
        </TextWrapper>
        <KakaoButton onClick={handleKakaoAuth}>
          <IconWrapper>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
              <path d="M10 0C15.5228 0 20 3.44404 20 7.69238C19.9999 11.9407 15.5228 15.3848 10 15.3848C9.43635 15.3848 8.88392 15.3463 8.3457 15.2773L3.55273 18.874C3.39944 18.989 3.18767 18.8434 3.24023 18.6592L4.5332 14.1318C1.80412 12.7583 3.35915e-05 10.3884 0 7.69238C0 3.44404 4.47715 0 10 0Z" fill="#181600"/>
            </svg>
          </IconWrapper>
          카카오 인증하기
        </KakaoButton>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default KakaoChannelModal;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContainer = styled.div`
  background-color: white;
  width: ${convertToVwWeb(400)};
  padding: ${convertToVwWeb(40)} ${convertToVwWeb(24)} ${convertToVwWeb(24)};
  border-radius: ${convertToVwWeb(16)};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwWeb(12)};
  position: relative;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    width: 300px;
    padding: 32px 20px 20px;
    border-radius: 16px;
    gap: 12px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${convertToVwWeb(16)};
  right: ${convertToVwWeb(16)};
  background: none;
  border: none;
  font-size: ${convertToVwWeb(28)};
  color: ${theme.colors.grayScale[40]};
  cursor: pointer;
  line-height: 1;

  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
    font-size: 24px;
  }
`;

const ImageWrapper = styled.div`
  width: ${convertToVwWeb(140)};
  height: ${convertToVwWeb(140)};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    width: 120px;
    height: 120px;
    margin-bottom: -10px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwWeb(4)};
  margin-top: -${convertToVwWeb(12)};

  @media (max-width: 768px) {
    margin-top: 0;
    gap: 4px;
  }
`;

const MainText = styled.p`
  color: #17171B;
  text-align: center;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 140%;
  }
`;

const KakaoButton = styled.button`
  width: 100%;
  height: ${convertToVwWeb(56)};
  background-color: #FEE500;
  border-radius: ${convertToVwWeb(12)};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwWeb(8)};
  cursor: pointer;
  
  ${theme.typography.contents.medium20};
  color: #191919;
  
  &:hover {
    background-color: #FDD835;
  }

  @media (max-width: 768px) {
    height: 48px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    margin-top: 8px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
